import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Topbar } from '@components/topbar'
import { Footer } from '@components/footer'
import { SEO } from '@components/seo'
import { useStoreActions, useStoreState } from '@stores'
import { CLIENT_CODE, SITE_URL, stripHtml, truncate } from '@constants'

const formatDate = (s?: string): string => {
	if (!s) return ''
	const d = new Date(s)
	if (isNaN(d.getTime())) return s
	return d.toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric'
	})
}

export const PromotionDetailPage = () => {
	const { slug } = useParams<{ slug: string }>()
	const { fetchAllPromo } = useStoreActions(action => action.promo)
	const { promos } = useStoreState(state => state.promo)
	const [activePhoto, setActivePhoto] = useState(0)

	useLayoutEffect(() => {
		window.scrollTo(0, 0)
	}, [slug])

	useEffect(() => {
		if (!promos || promos.length === 0) {
			fetchAllPromo({
				page: 1,
				limit: 100,
				client_code: CLIENT_CODE,
				status: 'all'
			})
		}
	}, [fetchAllPromo, promos])

	const promo = useMemo(
		() => promos?.find(p => p.slug === slug),
		[promos, slug]
	)

	useEffect(() => {
		setActivePhoto(0)
	}, [promo?.id])

	const dateLabel = promo
		? promo.period?.start && promo.period?.end
			? `${formatDate(promo.period.start)} – ${formatDate(promo.period.end)}`
			: formatDate(promo?.period?.start || promo?.period?.end)
		: ''

	const heroImage =
		promo?.photos && promo.photos.length > 0
			? promo.photos[activePhoto] || promo.photos[0]
			: null

	const promoJsonLd = promo
		? {
				'@context': 'https://schema.org',
				'@type': 'SpecialAnnouncement',
				name: promo.name,
				url: `${SITE_URL}/promotion/${promo.slug}`,
				datePosted: promo.period?.start || undefined,
				expires: promo.period?.end || undefined,
				announcementLocation: { '@id': `${SITE_URL}/#mall` },
				text: truncate(stripHtml(promo.description), 300),
				image: promo.photos && promo.photos.length > 0 ? promo.photos[0] : undefined,
				category: promo.promo_category?.name || undefined
			}
		: null

	const breadcrumb = promo
		? {
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: [
					{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
					{
						'@type': 'ListItem',
						position: 2,
						name: 'Promotions',
						item: `${SITE_URL}/promotion`
					},
					{
						'@type': 'ListItem',
						position: 3,
						name: promo.name,
						item: `${SITE_URL}/promotion/${promo.slug}`
					}
				]
			}
		: null

	return (
		<div className="min-h-screen bg-gray-50">
			{promo && (
				<SEO
					title={promo.name}
					description={truncate(
						stripHtml(promo.description) ||
							`${promo.name} di Botani Square Mall Bogor. Periode ${dateLabel || 'tersedia di halaman ini'}.`,
						180
					)}
					path={`/promotion/${promo.slug}`}
					type="article"
					image={
						promo.photos && promo.photos.length > 0 ? promo.photos[0] : undefined
					}
					publishedTime={promo.period?.start || undefined}
					jsonLd={[promoJsonLd!, breadcrumb!]}
				/>
			)}
			<Topbar />

			<section className="pt-28 pb-8 sm:pt-32 sm:pb-12">
				<div className="max-w-screen-lg mx-auto px-4 sm:px-6">
					<Link
						to="/promotion"
						className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
					>
						<span aria-hidden>←</span>
						Back to Promotions
					</Link>
				</div>
			</section>

			<section className="pb-16 sm:pb-24">
				<div className="max-w-screen-lg mx-auto px-4 sm:px-6">
					{!promo ? (
						<div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-500">
							Loading promo details…
						</div>
					) : (
						<article className="bg-white rounded-2xl shadow-sm overflow-hidden">
							<div className="relative aspect-[16/9] bg-gradient-to-br from-blue-100 to-indigo-100">
								{heroImage ? (
									<img
										src={heroImage}
										alt={promo.name}
										className="absolute inset-0 w-full h-full object-cover"
									/>
								) : (
									<div className="absolute inset-0 flex items-center justify-center text-6xl">
										🎉
									</div>
								)}
								{promo.promo_category?.name && (
									<span className="absolute top-4 left-4 bg-primary text-white text-xs font-medium uppercase tracking-wide px-3 py-1 rounded-full shadow">
										{promo.promo_category.name}
									</span>
								)}
							</div>

							{promo.photos && promo.photos.length > 1 && (
								<div className="flex gap-2 px-6 pt-4 overflow-x-auto">
									{promo.photos.map((src, i) => (
										<button
											key={`${src}-${i}`}
											onClick={() => setActivePhoto(i)}
											className={`relative w-20 h-14 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${
												i === activePhoto
													? 'border-primary'
													: 'border-transparent hover:border-gray-300'
											}`}
										>
											<img
												src={src}
												alt={`${promo.name} ${i + 1}`}
												className="w-full h-full object-cover"
											/>
										</button>
									))}
								</div>
							)}

							<div className="p-6 sm:p-10">
								{dateLabel && (
									<div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 mb-3">
										<svg
											className="w-4 h-4"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
												clipRule="evenodd"
											/>
										</svg>
										{dateLabel}
									</div>
								)}

								<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
									{promo.name}
								</h1>

								<div
									className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
									dangerouslySetInnerHTML={{
										__html: promo.description || ''
									}}
								/>

								<div className="mt-10 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
									<Link
										to="/promotion"
										className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:border-primary hover:text-primary transition-colors text-sm font-medium"
									>
										← Back to all promotions
									</Link>
									<span
										className={`inline-flex items-center text-xs font-medium px-3 py-1 rounded-full ${
											promo.active
												? 'bg-green-100 text-green-700'
												: 'bg-gray-100 text-gray-500'
										}`}
									>
										{promo.active ? 'Active' : 'Inactive'}
									</span>
								</div>
							</div>
						</article>
					)}
				</div>
			</section>

			<Footer />
		</div>
	)
}
